import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { backends } from '@/api/backends';
import { request } from '@/api/client';
import { AuthContext, type AuthContextValue, type LogoutReason } from './AuthContext';
import { NoDispoAccessError } from './errors';
import { PERMISSIONS, type Permission } from './permissions';
import { readStoredUser, SESSION_EXPIRED_EVENT, writeStoredUser } from './session';
import type { AuthUser } from './types';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(() => readStoredUser());
    const [logoutReason, setLogoutReason] = useState<LogoutReason | null>(null);
    // Server data is user-scoped (an admin's detail carries the VIN): the shared cache never
    // outlives the session it was fetched in.
    const queryClient = useQueryClient();

    useEffect(() => {
        const onExpired = () => {
            queryClient.clear();
            setUser(null);
            setLogoutReason('expired');
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, [queryClient]);

    const login = useCallback(
        async (loginName: string, password: string) => {
            const next = await request<AuthUser>(backends.core, '/auth/login-internal', {
                method: 'POST',
                body: { login: loginName, password, rememberMe: true },
            });
            if (next.permissions[PERMISSIONS.DISPO] !== true) {
                throw new NoDispoAccessError();
            }
            queryClient.clear();
            writeStoredUser(next);
            setUser(next);
            setLogoutReason(null);
        },
        [queryClient],
    );

    const logout = useCallback(() => {
        queryClient.clear();
        writeStoredUser(null);
        setUser(null);
        setLogoutReason('user');
    }, [queryClient]);

    const can = useCallback((permission: Permission) => user?.permissions[permission] === true, [user]);

    const value = useMemo<AuthContextValue>(
        () => ({ user, logoutReason, login, logout, can }),
        [user, logoutReason, login, logout, can],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
